/**
 * @description Serviço para criar eventos no Google Calendar
 * @module PostEvent
 * @since Março 2024
 */

import axios from 'axios';
import { endpoints } from '../../configs/Settings';
import { useAuth } from '../../utils/useAuth';
import { RefreshTokenGoogle } from './RefreshTokenGoogle';
import { useSessionContext } from '../../contexts/SessionContext';

export interface IEventAttendee {
  email: string;
  displayName?: string;
}

export interface IEventReminder {
  method: 'email' | 'popup';
  minutes: number;
}

export interface IEventData {
  summary: string;
  location?: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  attendees?: IEventAttendee[];
  reminders?: {
    useDefault: boolean;
    overrides?: IEventReminder[];
  };
}

interface PostEventParams {
  eventData: IEventData;
  accessTokenGoogle: string;
  refreshToken: string;
}

/**
 * Cria um novo evento no Google Calendar
 * @param params - Parâmetros necessários para criar o evento
 * @returns Promise com os dados do evento criado
 * @throws Error se a criação falhar
 */
export function usePostEvent() {
  const { setAccessTokenGoogle } = useSessionContext();

  const postEvent = async ({ 
    eventData, 
    accessTokenGoogle, 
    refreshToken
  }: PostEventParams) => {
    if (!accessTokenGoogle) {
      throw new Error('Token de acesso do Google não disponível');
    }

    try {
      const response = await axios.post(
        `${endpoints.GOOGLE}/calendar/v3/calendars/primary/events`,
        eventData,
        {
          headers: {
            'Authorization': await useAuth.getUserToken(),
            'Content-Type': 'application/json',
            'x-custom-google-auth': `Bearer ${accessTokenGoogle}`
          }
        }
      );

      if (!response.data) {
        throw new Error('Falha na criação do evento');
      }

      return response.data;
    } catch (error: any) {
      console.error('Erro detalhado:', error.response?.data || error.message);
      
      if (error.response?.status === 401) {
        try {
          // Tenta renovar o token do Google
          const googleTokenData = await RefreshTokenGoogle(refreshToken);
          
          // Atualiza o token no contexto
          setAccessTokenGoogle(googleTokenData.access_token);
          
          // Tenta criar o evento novamente com o novo token
          return await postEvent({
            eventData,
            accessTokenGoogle: googleTokenData.access_token,
            refreshToken
          });
        } catch (refreshError) {
          console.error('Erro ao renovar token do Google:', refreshError);
          throw new Error('Falha ao renovar token do Google');
        }
      }
      
      throw new Error(`Falha ao criar evento no Google Calendar: ${error.message}`);
    }
  };

  return { postEvent };
}
